import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { BehaviorSubject, combineLatest, ReplaySubject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { PAGE_NAMES, ROLES, ROLES_TYPE } from '../../../core/helpers/constants';
import { createEmptyGuid, createGuid, getRolesForPage, hasAdminRole, matchingPasswords } from '../../../core/helpers/utility';
import { IRole, IUser } from '../../../core/models/auth.models';
import { IClient } from '../../../core/models/clients.models';
import { UsersApiService } from '../../../pages/users/users.apiservice';
import { DataFeedService } from '../../api/datafeed.service';
import * as _ from 'underscore'
import { AppService } from '../../../store/app.service';
import { ICompany } from '../../../core/models/datafeed.models';
import Swal from 'sweetalert2';
import { ICampaign } from '../../../core/models/campaigns.model';
import { CampaignsApiService } from '../../../pages/campaigns/campaigns.apiservice';
import { AccountService } from '../../../account/account.service';
import { ToastService } from '../../../core/helpers/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { InstanceService } from '../../../core/instance/instance.service';

@Component({
  selector: 'modal-add-user',
  templateUrl: './add-user.component.html',
  styleUrls: ['./add-user.component.scss']
})
export class AddUserModal implements OnInit, OnDestroy {
  private readonly roleTitleKeyByCode: Record<number, string> = {
    [ROLES.Admin.Code]: 'ROLES.NAMES.ADMIN',
    [ROLES.TechServices.Code]: 'ROLES.NAMES.TECH_SERVICES',
    [ROLES.DMS.Code]: 'ROLES.NAMES.DMS',
    [ROLES.ShopManager.Code]: 'ROLES.NAMES.SHOP_MANAGER',
    [ROLES.CompanyOwner.Code]: 'ROLES.NAMES.COMPANY_OWNER',
    [ROLES.MarketingManager.Code]: 'ROLES.NAMES.MARKETING_MANAGER',
    [ROLES.ClientOwner.Code]: 'ROLES.NAMES.CLIENT_OWNER',
    [ROLES.Employee.Code]: 'ROLES.NAMES.EMPLOYEE',
    [ROLES.SubscriptionEmployee.Code]: 'ROLES.NAMES.SUBSCRIPTION_EMPLOYEE',
    [ROLES.SalesRep.Code]: 'ROLES.NAMES.SALES_REP',
    [ROLES.Staff.Code]: 'ROLES.NAMES.STAFF'
  };

  // Dev-mode NG0100 prevention: don't start loader-triggering HTTP requests during the initial CD pass.
  private initialLoadsQueued = false;
  private pendingUserIdToLoad: string | null = null;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  ROLES_TYPE = ROLES_TYPE;
  ROLES = ROLES;
  showTooltip = new Array<boolean>();
  @Output() oSaveClicked = new EventEmitter<any>();

  rawRoles: Array<IRole> = new Array<IRole>();
  rolesFiltered: Array<BehaviorSubject<Array<IRole>>> = new Array<BehaviorSubject<Array<IRole>>>();
  allCompanies: Array<ICompany> = new Array<ICompany>();
  allClients: Array<IClient> = new Array<IClient>();
  clientsData: Array<IClient> = new Array<IClient>();
  allCampaigns: Array<ICampaign> = new Array<ICampaign>();
  clientsFiltered: Array<Array<IClient>> = new Array<Array<IClient>>();
  campaignsFiltered: Array<Array<ICampaign>> = new Array<Array<ICampaign>>();

  addUserForm!: FormGroup;
  submitted = false;

  rolesOptions: Array<any> = [];

  myRoles: Array<number> = new Array<number>();
  isAdmin: boolean = false;
  isInternalUser: boolean = false;
  activeUser: IUser = {} as IUser;
  assignedRoleCodes: Array<number> = [];
  iCanCreate: Array<number> = [];
  subs = new Map<string | undefined, any>();
  canChangeRoles = false;

  @Input() set iId(value: string | null | undefined) {
    const userId = (value ?? '').trim();
    if (userId) {
      // Avoid starting HTTP here; it can trip NG0100 via ngx-ui-loader.
      this.setEditModeLocal(userId);
      this.pendingUserIdToLoad = userId;
    } else {
      this.pendingUserIdToLoad = null;
      this.setCreateMode();
    }

    this.queueInitialLoads();
  }
  id: string | null = null;

  constructor(
    private toastService: ToastService,
    private dataFeedService: DataFeedService,
    private userApiService: UsersApiService,
    public activeModal: NgbActiveModal,
    private campaignApiService: CampaignsApiService,
    private appService: AppService,
    private accountService: AccountService,
    private translate: TranslateService,
    public instance: InstanceService,
    private formBuilder: FormBuilder) {
    this.addUserForm = this.formBuilder.group({
      id: [null],
      firstName: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      password: ['', [Validators.required]],
      confirmPassword: [''],
      type: [null],
      companyId: [''],
      email: ['', [Validators.required, Validators.email]],
      sendEmail: [false],
      operation: 'create',
      roles: this.formBuilder.array([])
    }, { validator: matchingPasswords('password', 'confirmPassword') });

    this.appService.getActiveUser$()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(u => {
        this.activeUser = u;
        this.myRoles = u.roles.map(x => x.code);
        this.isAdmin = hasAdminRole(u.roles);
        // this.addUserForm.patchValue({ type: u.type });
        this.addUserForm.patchValue({ type: 2 }); // default to External User
        var r = getRolesForPage(u.roles, PAGE_NAMES.Users);
        this.canChangeRoles = this.isAdmin || u.iCanCreate.length > 0;
      });
  }

  private formatFullName(firstName?: string, lastName?: string): string {
    const first = (firstName ?? '').trim();
    const last = (lastName ?? '').trim();
    return (this.instance.isLastNameFirst ? `${last} ${first}` : `${first} ${last}`).trim();
  }
  get f() { return this.addUserForm.controls; }

  public get roles(): FormArray {
    return this.addUserForm.get('roles') as FormArray
  }
  addRole(id?: string, roleId?: string, companyId?: string, clientId?: string, campaignId?: string, code?: number) {
    id = id == undefined ? createGuid() : id;
    this.rolesFiltered.push(new BehaviorSubject<Array<IRole>>([]));
    let index = this.rolesFiltered.length - 1;
    const sub = combineLatest([
      this.appService.getAllClientsFeed$(),
      this.appService.getRoleRestrictions$(),
      this.appService.getAllRoles$(),
      this.campaignApiService.getCampaignsNamesByClient(clientId ?? createEmptyGuid()),
      this.appService.getAllClients$()
    ]).pipe(
      takeUntil(this.destroyed$),
      filter(x => x[0].length > 0 && x[1].length > 0 && x[2].length > 0),
    ).subscribe((r: any) => {
      let roleRestrictions: any = r[1];
      let allRoles = this.rawRoles = [...r[2]];

      if (this.instance.isAltLayout) {
        // Filter out specific roles
      const excludedCodes = [ROLES.SubscriptionEmployee.Code, ROLES.ShopManager.Code, ROLES.SalesRep.Code, ROLES.CompanyOwner.Code, ROLES.MarketingManager.Code];
      allRoles = allRoles.filter(role => !excludedCodes.includes(role.code));
      this.rawRoles = allRoles;
      }

      this.assignedRoleCodes = allRoles.filter(y => this.roles.value.map((x: any) => x.roleId).includes(y.id)).map(x => x.code);

      let response: any;
      if (!this.isInternalUser) {
        if (r[4].data.length > 0) {
          response = r[0].filter((item: any) => 
            r[4].data.some((dataItem: any) => dataItem.id === item.id)
          );
        } else {
          response = null;
        }
      } else {
        response = r[0];
      }
      this.allClients = [...response].map(i => { i.fullName = this.formatFullName(i.firstName, i.lastName); return i; });

      if (companyId)
        this.clientsFiltered.push([...this.allClients.filter(x => x.brandId === companyId).map((i: any) => { i.fullName = this.formatFullName(i.firstName, i.lastName); return i; })]);
      else
        this.clientsFiltered.push([...this.allClients.map((i: any) => { i.fullName = this.formatFullName(i.firstName, i.lastName); return i; })]);

      // if (campaignId) {
      this.campaignsFiltered.push(r[3].data as Array<ICampaign>)
      // }

      this.roles.push(this.newRole(id, roleId, companyId, clientId, campaignId, code));
      if (this.assignedRoleCodes.length === 0 || this.assignedRoleCodes.every(x => x === ROLES.Admin.Code))
        this.rolesFiltered[index].next(allRoles!)
      else {
        let fr: Array<IRole> = [];
        for (let role of allRoles) {
          for (let aes of this.assignedRoleCodes) {
            let ress = roleRestrictions.find((x: any) => x.userRoleCode == aes);
            if (ress && ress.iCanBe !== null)
              if (ress.iCanBe.includes(role.code)) {
                fr.push(role);
              }
            if (role.code == aes && role.code !== ROLES.Admin.Code && !fr.some(x => x.code === role.code)) {
              fr.push(role);
            }
          }
        }
        this.rolesFiltered[index].next(fr)
      }
      this.subs.get(id).unsubscribe();
      this.syncRolesUI();
    });
    this.subs.set(id, sub);

  }
  newRole(id?: string, roleId?: string, companyId?: string, clientId?: string, campaignId?: string, code?: number): FormGroup {
    this.rolesOptions.push({});
    return this.formBuilder.group({
      code: code,
      id: [id || createGuid()],
      roleId: [roleId || null, Validators.required],
      companyId: companyId || null,
      clientId: clientId || null,
      campaignId: campaignId || null
    })
  }
  deleteRole(i: number) {
    this.rolesOptions.splice(i, 1);
    this.roles.removeAt(i);
    this.clientsFiltered.splice(i, 1);
    this.rolesFiltered.splice(i, 1)
  }
  onRoleChange(evt: any, index: number) {
    if (index == 0)
      for (let i = 0; i < this.roles.value.length; i++) {
        if (i !== 0)
          this.deleteRole(i);
      }

    this.appService.getAllRoles$()
      .pipe(take(1))
      .subscribe((allRoles: Array<IRole>) => {
        this.rawRoles = allRoles;
        var selectedRoleId = this.roles.at(index)?.get('roleId')?.value;
        this.showTooltip[index] = [ROLES.Employee.Code, ROLES.SubscriptionEmployee.Code].includes(this.getRoleCodeByRoleId(selectedRoleId))

        var roleCode: any = allRoles.find(x => x.id == evt)?.code;
        let roleAttr: any = _.find(ROLES, function (item) {
          return item.Code == roleCode
        })
        if (roleAttr?.Company) {
          this.rolesOptions[index] = { ...this.rolesOptions[index], Company: roleAttr.Company.Required };
          //if (this.myRoles.some(r => [ROLES.MarketingManager.Code, ROLES.CompanyOwner.Code].includes(r)))
          if(roleAttr.Company.Required){
            this.roles.at(index)?.get('companyId')?.setValidators([Validators.required]);
          }
          else{
            this.roles.at(index)?.get('companyId')?.clearValidators();
            this.roles.at(index)?.get('companyId')?.updateValueAndValidity();
          }
        }
        else {
          delete this.rolesOptions[index].Company;
          this.roles.at(index)?.get('companyId')?.clearValidators();
          this.roles.at(index)?.get('companyId')?.updateValueAndValidity();
        }

        if (roleAttr?.Client) {
          this.rolesOptions[index] = { ...this.rolesOptions[index], Client: roleAttr.Client.Required };
          if(roleAttr.Client.Required){
            this.roles.at(index)?.get('clientId')?.setValidators([Validators.required]);
          }
          else{
            this.roles.at(index)?.get('clientId')?.clearValidators();
            this.roles.at(index)?.get('clientId')?.updateValueAndValidity();
          }
        }
        else {
          delete this.rolesOptions[index].Client;
          this.roles.at(index)?.get('clientId')?.clearValidators();
          this.roles.at(index)?.get('clientId')?.updateValueAndValidity();
        }

        if (roleAttr?.Campaign) {
          this.rolesOptions[index] = { ...this.rolesOptions[index], Campaign: roleAttr.Campaign.Required };
          if(roleAttr.Campaign.Required){
            this.roles.at(index)?.get('campaignId')?.setValidators([Validators.required]);
          }
          else{
            this.roles.at(index)?.get('campaignId')?.clearValidators();
            this.roles.at(index)?.get('campaignId')?.updateValueAndValidity();
          }
        }
        else {
          delete this.rolesOptions[index].Campaign;
          this.roles.at(index)?.get('campaignId')?.clearValidators();
          this.roles.at(index)?.get('campaignId')?.updateValueAndValidity();
        }
      });
  }
  onRoleCompanyChange(evt: any, index: number) {
    if (typeof this.clientsFiltered[index] !== 'undefined' && evt.id !== null) {
      this.clientsFiltered[index] = [...this.allClients.filter(x => x.brandId === evt.id).map((i: any) => { i.fullName = this.formatFullName(i.firstName, i.lastName); return i; })]
    }
  }
  onRoleClientChange(evt: any, index: number) {
    this.campaignApiService.getCampaignsNamesByClient(evt.id)
      .pipe(take(1))
      .subscribe((response: any) => {
        if (response.good) {
          this.campaignsFiltered[index] = response.data;
        }
      });
  }
  syncRolesUI() {
    let index = 0;
    for (var role of this.f.roles.value) {
      let roleAttr: any = _.find(ROLES, (item) => {
        let code = this.getRoleCodeByRoleId(role.roleId);
        return item.Code == code
      })
      if (roleAttr?.Company) {
        this.rolesOptions[index] = { ...this.rolesOptions[index], Company: roleAttr.Company.Required };
        //if (this.myRoles.some(r => [ROLES.MarketingManager.Code, ROLES.CompanyOwner.Code].includes(r)))
        this.roles.at(index)?.get('companyId')?.setValidators([Validators.required]);
      }
      else {
        delete this.rolesOptions[index].Company;
        this.roles.at(index)?.get('companyId')?.clearValidators();
      }

      if (roleAttr?.Client) {
        this.rolesOptions[index] = { ...this.rolesOptions[index], Client: roleAttr.Client.Required };
        // this.roles.at(index)?.get('clientId')?.setValidators([Validators.required]);
      }
      else {
        delete this.rolesOptions[index].Client;
        // this.roles.at(index)?.get('clientId')?.clearValidators();
      }

      if (roleAttr?.Campaign) {
        this.rolesOptions[index] = { ...this.rolesOptions[index], Campaign: roleAttr.Campaign.Required };
        // this.roles.at(index)?.get('campaignId')?.setValidators([Validators.required]);
      }
      else {
        delete this.rolesOptions[index].Campaign;
        // this.roles.at(index)?.get('campaignId')?.clearValidators();
      }
      index++;
    }
  }

  updateData(data: IClient) {
    this.addUserForm.patchValue(data);
  }

  getRoleTitle(role: IRole | null | undefined): string {
    if (!role) return '';

    const mappedKey = this.roleTitleKeyByCode[role.code];
    if (mappedKey) {
      const mapped = this.translate.instant(mappedKey);
      if (mapped && mapped !== mappedKey) return mapped;
    }

    const rawTitle = (role.title ?? '').toString();
    if (!rawTitle) return '';
    const translated = this.translate.instant(rawTitle);
    return translated && translated !== rawTitle ? translated : rawTitle;
  }
  getRoleCodeByRoleId(roleId: string) {
    return this.rawRoles.find(x => x.id === roleId)?.code!
  }

  ngOnInit() {
    // If the modal is opened without binding iId at all, Angular won't call the setter.
    // Default to create-mode in that case.
    if (!this.id && this.f.operation.value !== 'edit') {
      this.setCreateMode();
    }

    const user = this.appService.getActiveUser$().value;
    this.isInternalUser = user.type == 1;

    this.queueInitialLoads();
  }
  ngAfterViewInit() {
    this.assignedRoleCodes = this.roles.value.map((x: any) => x.code);
  }

  loadUser() {
    if (this.id) {
      this.loadUserDetails(this.id);
    }
  }

  private setEditModeLocal(userId: string): void {
    this.id = userId;
    this.f.operation.setValue('edit');
    this.f.password.clearValidators();
    this.f.confirmPassword.clearValidators();
    this.f.password.updateValueAndValidity({ emitEvent: false });
    this.f.confirmPassword.updateValueAndValidity({ emitEvent: false });
  }

  private loadUserDetails(userId: string): void {
    this.userApiService.getUserDetails(userId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: any) => {
        this.addUserForm.patchValue(response.data);
        for (var role of response.data.roles) {
          this.addRole(role.id, role.roleId, role.companyId, role.clientId, role.campaignId, role.code)
        }
        this.syncRolesUI();
      });
  }

  private queueInitialLoads(): void {
    if (this.initialLoadsQueued) return;
    this.initialLoadsQueued = true;

    // Defer to the next macrotask so ngx-ui-loader class bindings don't change during Angular's initial check.
    setTimeout(() => {
      this.appService.fetchRoleRestrictions();
      this.appService.fetchAllClientsFeed();
      this.appService.fetchAllClients(
        { SavedSearchId: '', endDate: null, pageNumber: 1, pageSize: null, search: "", sortColumn: "lastName,firstName", sortDirection: "asc", startDate: null }
      );
      this.appService.fetchAllRoles();
      this.dataFeedService.getAllCompanies()
        .pipe(takeUntil(this.destroyed$))
        .subscribe((response: any) => {
          if (response.good) {
            this.allCompanies = [...response.data];
          }
        });

      if (this.pendingUserIdToLoad) {
        const userId = this.pendingUserIdToLoad;
        this.pendingUserIdToLoad = null;
        this.loadUserDetails(userId);
      }
    }, 0);
  }

  private setCreateMode(): void {
    this.id = null;
    this.f.operation.setValue('create');
    this.f.password.setValidators([Validators.required]);
    this.f.confirmPassword.setValidators([Validators.required]);
    this.f.password.updateValueAndValidity({ emitEvent: false });
    this.f.confirmPassword.updateValueAndValidity({ emitEvent: false });

    const existingPassword = (this.f.password.value ?? '').toString();
    const existingConfirm = (this.f.confirmPassword.value ?? '').toString();
    if (!existingPassword && !existingConfirm) {
      const generatedPassword = this.generateSecurePassword(20);
      this.f.password.setValue(generatedPassword);
      this.f.confirmPassword.setValue(generatedPassword);
    }
  }

  private generateSecurePassword(length: number): string {
    const minLength = 12;
    const targetLength = Math.max(length, minLength);

    const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
    const lower = 'abcdefghijkmnopqrstuvwxyz';
    const digits = '23456789';
    const symbols = '!@#$%^&*()-_=+[]{};:,.?';
    const all = upper + lower + digits + symbols;

    const pick = (chars: string) => chars[this.secureRandomInt(chars.length)];

    const passwordChars: string[] = [pick(upper), pick(lower), pick(digits), pick(symbols)];
    while (passwordChars.length < targetLength) {
      passwordChars.push(pick(all));
    }

    for (let i = passwordChars.length - 1; i > 0; i--) {
      const j = this.secureRandomInt(i + 1);
      [passwordChars[i], passwordChars[j]] = [passwordChars[j], passwordChars[i]];
    }

    return passwordChars.join('');
  }

  private secureRandomInt(maxExclusive: number): number {
    if (maxExclusive <= 0) return 0;
    const cryptoObj: Crypto | undefined = (globalThis as any)?.crypto;
    if (!cryptoObj?.getRandomValues) {
      // Fallback (non-cryptographic) — should be extremely rare in modern browsers.
      return Math.floor(Math.random() * maxExclusive);
    }

    const maxUint32 = 0x1_0000_0000;
    const limit = maxUint32 - (maxUint32 % maxExclusive);
    const arr = new Uint32Array(1);
    let x = 0;
    do {
      cryptoObj.getRandomValues(arr);
      x = arr[0];
    } while (x >= limit);
    return x % maxExclusive;
  }

  onSubmit() {
    this.submitted = true;
    if (this.f.operation.value === 'create' && this.f.roles.value.length === 0) {
      this.f.roles.setErrors({ 'required': true });
    }
    if (this.addUserForm.invalid)
      return;
    if (this.f.id.value === null)
      this.f.id.setValue(createGuid());

    if (this.f.operation.value === 'edit' && this.f.roles.value.length === 0 && this.canChangeRoles) {
      Swal.fire({
        title: this.translate.instant('ARE_YOU_SURE'),
        text: this.translate.instant('USER_NO_ROLES_DELETE_WARNING'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: this.translate.instant('YES_DELETE_IT')
      }).then((result) => {
        if (result.isConfirmed) {
          this.oSaveClicked.emit(this.addUserForm.value);
        }
      })
    }
    else {
      var request = this.addUserForm.value;
      request = this.trimObjectStrings(request);
      this.oSaveClicked.emit(request);
    }
  }

  // This function will recursively iterate through the object
  trimObjectStrings(obj: any): any {
    // Iterate over each property of the object
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const value = obj[key];
        
        if (typeof value === 'string') {
          // Trim the string if the value is a string
          obj[key] = value.trim();
        } else if (typeof value === 'object' && value !== null) {
          // Recursively call the function if the value is an object (or array)
          this.trimObjectStrings(value);
        }
      }
    }
    return obj;
  }

  onAddUserTooltipShown(index: number) {
    var sub = this.rolesFiltered[index]
      .subscribe(roles => {
        // this.showTooltip = 
      })
    sub.unsubscribe();
  }
  sendPasswordResetEmail() {
    let email = this.f.email.value;
    this.addUserForm.markAllAsTouched();
    if (this.f.email.valid) {
      this.toastService.showStandard(this.translate.instant('SENDING_EMAIL_PLEASE_WAIT'));
      this.accountService.requestResetPassword(email)
        .pipe(takeUntil(this.destroyed$)
        )
        .subscribe(response => {
          if (response.good) {
            this.toastService.showSuccess(this.translate.instant('EMAIL_SENT'));
          }
        });
    }
    else {
      this.toastService.showDanger(this.translate.instant('INVALID_EMAIL_ADDRESS'));
    }

  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

}
